import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, casestudyCards } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const Burgerman: React.FC = () => {
    return (
        <NewLayout
            title="Young Franchise Holdings LLP boosts customer retention and growth"
            description="Discover how Young Franchise Holdings LLP transformed customer retention, cut costs, and scaled its customer base 5x using Gallabox’s broadcast feature."
            canonical="https://gallabox.com/case-study/burgerman"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/burgerman/burgerman-og.png" }}
        >
            <CaseStudyHerosection
                title="Young Franchise Holdings LLP boosts customer retention and business growth with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/burgerman/burgerman-hero.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "85%", label: "improvement in overall business growth" },
                    { percentage: "5x", label: "reduction in marketing and advertising spend" },
                    { percentage: "5X", label: "increase in customer base" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Food & Beverage"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [{ link: "https://gallabox.com/whatsapp-broadcast", label: "WhatsApp Broadcast" }],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About">
                            <p>
                                With years of hands-on experience in the fast-food industry, Young Franchise Holdings LLP is a close-knit team passionate about helping restaurants
                                and food businesses grow sustainably. They bring business goals to life by offering brand guidance, staff training, proven insights, and supply
                                chain support — all wrapped up in effective management strategies. Their flagship brand, Burgerman, has become a name known for delivering great
                                taste and consistent quality, making it a favorite among burger lovers looking for quick, satisfying meals.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"Challenges before Gallabox"} className="mt-0" />

                            <p className="mt-2">
                                Running a fast-food business like Burgerman means juggling multiple demands — from handling customer orders quickly to managing promotions and
                                keeping the brand consistent across locations. Before switching to Gallabox, Young Franchise Holdings LLP faced several communication and
                                operational hurdles that are all too familiar to fast-food operators:
                            </p>
                            <FeatureList
                                List={[
                                    {
                                        listItem:
                                            "Slow approval processes with their previous tool, Reelo, often meant waiting more than 48 hours for critical updates or campaign permissions. In the fast-food world, where trends and customer preferences shift quickly, this kind of delay can translate directly into missed marketing opportunities and slower response times.",
                                    },
                                    {
                                        listItem:
                                            "No WhatsApp support or dedicated point of contact made it difficult to handle real-time customer queries and internal coordination, especially during peak hours or promotional campaigns when timing is everything.",
                                    },
                                    {
                                        listItem:
                                            "The team struggled to retain customers in a highly competitive space where convenience, speed, and engagement are key. Without an efficient communication platform, they found it challenging to keep their loyal customers engaged and coming back for repeat orders.",
                                    },
                                    {
                                        listItem:
                                            "Managing repeat queries manually — such as order status, menu availability, and store locations — drained valuable staff time, taking away focus from creating great in-store experiences and personalized outreach to high-value customers.",
                                    },
                                ]}
                                className="mt-4 bg-gray-100 mb-3"
                            />
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Why Young Franchise Holdings chose Gallabox?"} className="mt-0" />
                            <p className="mt-2">
                                Gallabox stepped in as a game-changer for Young Franchise Holdings LLP. The platform’s user-friendly interface and strong backend support made it
                                easy for the team to get started and maintain momentum, even during busy times. By leveraging Gallabox’s broadcast feature, they could:
                            </p>

                            <FeatureList
                                List={[
                                    {
                                        listItem:
                                            "Send timely updates, offers, and promotions directly to customers on WhatsApp, a channel where fast-food customers expect quick, convenient communication.",
                                    },
                                    {
                                        listItem:
                                            "Automate responses to frequently asked questions like order tracking and menu details, freeing staff to focus on delivering better service and crafting personalized customer experiences.",
                                    },
                                    {
                                        listItem:
                                            "Get immediate support and responsive service from the Gallabox team, helping them quickly adapt and scale their customer engagement efforts without technical roadblocks.",
                                    },
                                ]}
                                className="mt-4 bg-gray-100 mb-3"
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"Impact on their business growth"} className="mt-0" />
                            <p className="mt-2">The impact was refreshing and measurable. Since adopting Gallabox, Young Franchise Holdings LLP has:</p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "Their customer footfall has grown to five times what it was before, bringing many more regular diners through the doors.",
                                    },
                                    {
                                        listItem:
                                            "They’ve managed to reduce their marketing costs by 80%, thanks to focused, smarter communication that reaches exactly the right audience.",
                                    },
                                    {
                                        listItem: "The number of engaged users interacting with their brand has increased by fivefold, boosting repeat visits and loyalty.",
                                    },
                                    {
                                        listItem:
                                            "Gallabox earned a strong 8.5 out of 10 rating from the team for ease of use and support—an essential factor for smooth operations in a busy kitchen environment.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation subTitle={"Looking ahead"} className="mt-10" />
                            <p className="mt-2">
                                Looking ahead, Young Franchise Holdings LLP plans to deepen their partnership with Gallabox to further enhance customer retention and operational
                                agility. For fast-food operators like Burgerman, the ability to engage customers instantly and maintain consistent communication across channels is
                                key to standing out in a crowded market.{" "}
                            </p>

                            <p className="mt-5">
                                With Gallabox supporting their daily customer conversations and promotions, Young Franchise Holdings LLP is well-positioned to serve more customers,
                                improve brand loyalty, and continue delivering the quick, tasty experiences that Burgerman fans love.
                            </p>

                            <p className="mt-5">
                                By turning everyday chats into meaningful business growth, Gallabox has become an indispensable tool in Young Franchise Holdings LLP’s recipe for
                                success in the fast-food industry.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCards.slice(0, 3)?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default Burgerman;
